/**
 * The approval chain, as the platform holds it (build 4, 0090).
 *
 * The Approvals board's requests — work, or a purchase stated by its value —
 * signed step by step: Engineering, Contracts, Purchasing, Finance, COO, CEO.
 * Each step is its own permission (`approval:<step>`). Who signs is the person
 * signed in; one person signs one step of a request, and nobody signs their
 * own. The rules and the sentences are the platform's (backend
 * stores/service.py), and the mock repeats them word for word, because a
 * refusal that reads differently in the demo is a second rule.
 *
 * Pure: types and the rules, no React, no demo domain.
 */

/** One step of the chain, and whether the person reading holds it. */
export interface ApprovalStepDef {
  key: string;
  label: string;
  /** who signs it: "Finance manager" */
  who: string;
  /** null: no limit */
  limit: number | null;
  permission: string;
  mine: boolean;
}

/** A step somebody signed. */
export interface ApprovalStepSigned {
  step: string;
  decision: "approved" | "rejected";
  by: string;
  at: string;
  note?: string;
  limit?: number;
  /** approved above the step's limit */
  over?: boolean;
}

export type ApprovalStatus = "submitted" | "approved" | "rejected" | "ordered" | "cancelled" | "draft";

export interface ApprovalOrder {
  number: string;
  at: string;
  by: string;
  supplier: string;
  value: number;
  status: string;
}

/** A request in the chain, as the platform reads it to the person asking. */
export interface ApprovalRequest {
  number: string;
  kind: string;
  title: string;
  supplier?: string;
  value: number;
  currency: string;
  why: string;
  source?: string;
  emergency: boolean;
  raisedAt: string;
  raisedBy: string;
  status: ApprovalStatus;
  steps: ApprovalStepSigned[];
  /** the step it waits on, while it waits */
  next?: { key: string; label: string; who: string } | null;
  /** may the person asking sign that step */
  mayAct: boolean;
  /** why not, in the words the refusal uses */
  whyNot?: string;
  order?: ApprovalOrder;
}

export interface ApprovalsBoard {
  currency: string;
  /** the name the platform stamps on whatever this person signs */
  me?: string;
  chain: ApprovalStepDef[];
  can: { raise: boolean; order: boolean };
  requests: ApprovalRequest[];
  /** request numbers whose next step this person may sign */
  awaitingMe: string[];
  /** the Store & Warehouse desk's own requisitions, decided there */
  stores: { waiting: number; approved: number };
}

export interface NewApprovalRequest {
  kind: string;
  title: string;
  supplier?: string;
  value: number | null;
  why: string;
  source?: string;
  emergency?: boolean;
}

export interface ApprovalStepAck {
  number: string;
  step: string;
  decision: "approved" | "rejected";
  status: ApprovalStatus;
  over: boolean;
  replayed?: boolean;
}

/** The platform's chain (stores/service.py APPROVAL_CHAIN). The limits are PROPOSED. */
export const APPROVAL_STEPS: readonly Omit<ApprovalStepDef, "mine">[] = [
  { key: "eng", label: "Engineering", who: "Engineering manager", limit: 250_000, permission: "approval:engineering" },
  { key: "contr", label: "Contracts", who: "Contracts manager", limit: 500_000, permission: "approval:contracts" },
  { key: "proc", label: "Purchasing", who: "Purchasing manager", limit: 1_000_000, permission: "approval:purchasing" },
  { key: "fin", label: "Finance", who: "Finance manager", limit: 5_000_000, permission: "approval:finance" },
  { key: "coo", label: "COO", who: "Chief Operating Officer", limit: 20_000_000, permission: "approval:coo" },
  { key: "ceo", label: "CEO", who: "Chief Executive Officer", limit: null, permission: "approval:ceo" },
];

/** The kinds the board raises (demo/domain/tables.ts REQ_TYPES). */
export const REQUEST_KINDS = ["maint", "project", "emergency", "outscope", "purchase", "variation"] as const;

/** The step a request waits on: the first not signed, none once one is rejected. */
export function nextStep(signed: readonly Pick<ApprovalStepSigned, "step" | "decision">[]) {
  if (signed.some((s) => s.decision === "rejected")) return null;
  const done = new Set(signed.map((s) => s.step));
  return APPROVAL_STEPS.find((s) => !done.has(s.key)) ?? null;
}

/** What raising a request refuses, in the platform's words; null when it may be raised. */
export function raiseRefusal(input: NewApprovalRequest): string | null {
  if (!(REQUEST_KINDS as readonly string[]).includes(input.kind))
    return "A request is new maintenance work, a new project, an emergency repair, out of scope work, a purchase or a contract variation.";
  if (!input.title?.trim()) return "A request needs a title: what is to be done, and where.";
  if (input.value == null || !Number.isFinite(input.value) || input.value <= 0)
    return "A request needs its value. Nobody can approve an amount nobody has stated.";
  if (!input.why?.trim())
    return "A request must say why it is needed. A purchase nobody can account for is one nobody approves twice.";
  return null;
}

/**
 * Why `me` may not sign the request's next step, or null when they may — the
 * platform's `_why_not`, sentence for sentence. `status` is the HTTP status the
 * platform refuses with.
 */
export function stepRefusal(
  req: Pick<ApprovalRequest, "number" | "status" | "raisedBy"> & { steps: Pick<ApprovalStepSigned, "step" | "decision" | "by">[] },
  me: { name: string; holds: (permission: string) => boolean },
): { why: string; status: number } | null {
  const step = nextStep(req.steps);
  if (req.status !== "submitted" || !step)
    return { why: `${req.number} is ${req.status} and is not waiting on a decision.`, status: 409 };
  if (req.raisedBy === me.name) return { why: `You raised ${req.number}. Nobody approves their own request.`, status: 409 };
  const mine = req.steps.find((s) => s.by === me.name);
  if (mine) {
    const label = APPROVAL_STEPS.find((s) => s.key === mine.step)?.label ?? mine.step;
    return { why: `You signed the ${label} step of ${req.number}. One person signs one step.`, status: 409 };
  }
  if (!me.holds(step.permission))
    return {
      why: `The ${step.label} step is the ${step.who}'s. Your roles do not hold ${step.permission}.`,
      status: 403,
    };
  return null;
}
